"use strict"
var DataStream = require('./DataStream')
var EventEmitter = require('events').EventEmitter
var WebSocket = require('isomorphic-ws')
var WsJsonStream = require('websocket-json-stream')
var DispatcherStream = require( 'dispatcher-stream' )
var Writable = require('stream').Writable
var inspect = require('util').inspect
var clone = require('lodash.clone')
var foreach = require('lodash.foreach')

const RETRY_DELAYS = [1000]

var getRetryDelay = function (retries, retryDelays) {
  var index = (retries < retryDelays.length) ? retries : retryDelays.length - 1
  return retryDelays[index]
}

var getRetryMessage = function (time) {
  var seconds = time / 1000
  var plural = (seconds === 1) ? '' : 's'
  return "Websocket connection lost. Retrying connection in " + seconds + " second" + plural
}

var resolveUrl = function (url) {
  return function () { return Promise.resolve(url) }
}

function PwStreamingClient (options) {
  if (!(this instanceof PwStreamingClient)) {
    return new PwStreamingClient(options)
  }
  options = options || {}
  if (typeof options.url === 'string') {
    this._getUrl = resolveUrl(options.url)
  } else if (typeof options.url === 'function') {
    this._getUrl = options.url
  } else {
    throw new Error("Must specify a url when constructing a streaming client")
  }
  this.retryDelays = options.retryDelays || RETRY_DELAYS
  this.logToConsole = (options.logToConsole == null) ? true : options.logToConsole
  this.streams = {}
  this.retries = 0
  this._boundOnError = this.onError.bind(this)
  this._boundOnConnect = this.onConnect.bind(this)
  this._boundOnClose = this.onClose.bind(this)
  this.initJsonStream()
  this.getUrl().then(this.initWebsocket.bind(this))
}

var proto = PwStreamingClient.prototype

proto.create = function (message) {
  var createMessage = {
    action: 'create',
    name: message.name,
    path: message.path,
    options: clone(message.options)
  }
  return this.createDataStream(createMessage)
}

proto.getDataStream = function (name) {
  return this.streams[name]
}

proto.createDataStream = function (createMessage) {
  var name = createMessage.name
  var streams = this.streams
  if (streams[name]) { throw new Error("Stream name already exists: " + name)}

  var dataStream = DataStream({
    createMessage: createMessage,
    jsonStream: this.jsonStream,
  })
  var apiStream = this.wsDispatcher.addOutput(name)
  apiStream.pipe(dataStream)

  streams[name] = dataStream
  var self = this
  dataStream.on('destroyed', function () {
    delete streams[name]
    self.wsDispatcher.removeOutput(name)
  })

  return dataStream
}

proto.initJsonStream = function () {
  var self = this
  this.jsonStream = WsJsonStream()

  var wsDispatcher = this.wsDispatcher = DispatcherStream({
    dispatch: function (data) { return data.name},
    defaultHandler: function (data) {
      console.log("Unknown message received" + inspect(data, {depth: null}))
    }
  })

  wsDispatcher.name = "WS Dispatcher"
  this.jsonStream.pipe(wsDispatcher, {end: false})

  //receing a heartbeat is a no op. It exists to make sure the connection doesn't stay
  //idle for too long or else HA proxy will end the connection.
  this.heartbeatStream = wsDispatcher.addOutput('heartbeat')
  this.heartbeatStream.on('data', function () {})

  this.messageStream = wsDispatcher.addOutput('messages')
  var messageSink = Writable({objectMode: true})
  messageSink._write = function (data, enc, next) {
    if (!self.logToConsole) { return next() }
    var data = data.data
    if (data.level === 'error') {
      console.error(data.message)
    } else {
      console.log(data.message)
    }
    next()
  }
  messageSink.name = "Console Logger"
  this.messageStream.pipe(messageSink)
}

proto.getUrl = function () {
  var self = this
  return this._getUrl().catch(function () {
    var retryDelay = getRetryDelay(self.retries, self.retryDelays)
    setTimeout(function () {
      self.retries += 1
      self.onClose()
    }, retryDelay)
    return Promise.reject()
  })
}

proto.initWebsocket = function (url) {
  var ws = this.ws = new WebSocket(url)

  if (!(ws instanceof EventEmitter)) {
    //browser version of WebSocket client
    ws.on = ws.addEventListener
    ws.removeListener = ws.removeEventListener
    ws.once = EventEmitter.prototype.once
  }
  ws.on('open', this._boundOnConnect)
  ws.on('error', this._boundOnError)
  ws.on('close', this._boundOnClose)
  this.jsonStream.setWs(this.ws)
}

proto.cleanupClosedWebsocket = function () {
  if (!this.ws) { return }
  this.ws.removeListener('open', this._boundOnConnect)
  this.ws.removeListener('error', this._boundOnError)
  this.ws.removeListener('close', this._boundOnClose)
}

proto.onConnect = function () {
  if (this.connectedAtLeastOnce) {
    this.resendAllCreateMessages()
    console.log("websocket reconnected")
  } else {
    console.log("websocket connected")
  }
  this.connectedAtLeastOnce = true
  this.retries = 0
}

proto.onError = function (err) {
  console.log("websocket error:", err)
}

proto.resendAllCreateMessages = function () {
  foreach(this.streams, function (dataStream) {
    dataStream.sendCreateMessage()
  })
}

proto.onClose = function () {
  this.cleanupClosedWebsocket()
  var retryDelay = getRetryDelay(this.retries, this.retryDelays)
  console.log(getRetryMessage(retryDelay))
  var self = this
  this.getUrl().then(function (url) {
    setTimeout(function () {
      self.retries += 1
      self.initWebsocket(url)
    }, retryDelay)
  })
}

proto.destroy = function () {
  if (this.destroyed) { return }
  this.destroyed = true
  this.cleanupClosedWebsocket()
  if (this.ws) { this.ws.close() }
}

module.exports = PwStreamingClient
